<script lang="ts">
	import {
		Table,
		Button,
		IconInput,
		CheckboxSingle,
		DisplayModal,
		Image,
		IconButton,
		Label,
		Dropdown
	} from '@cf/ui-lib';
	import { Export, MdBlock, StopIcon } from '@cf/ui-lib/icons';
	import { createDomainColumns, domainsColumns } from '$lib/columns/domains';
	import {
		useDomainsReadQuery,
		useDomainsCreateMutation,
		useDomainsRemoveMutation,
		useDomainsCloneMutation,
		useDomainsExportMutation,
		useChangeDomainsStatusMutation
	} from '$lib/api-v2/domainsQuery';
	import { useBrandReadQuery } from '$lib/api-v2/brandQuery';
	import { onMount } from 'svelte';
	import { writable } from 'svelte/store';
	import { debounce } from '$lib/utils/debounce.js';
	import { useSellersExportQuery } from '$lib/api-v2/sellersQuery.js';
	import { showToast } from '@cf/ui-lib/utils';
	import { status } from '$lib/mocks/singleBrand-details.js';
	import Modal from '$lib/components/modal.svelte';

	export let data;

	let searchValue = '';
	let pageSize = 10;
	let pageNumber = 1;
	let selectedRowIds: string[] = [];
	let isSelectAll: boolean = false;
	let domainError = '';
	let isReportDomainOpen = false;
	let domainDelteConformationModel = false;
	let itemToBeDeleted: null | string = null;
	$: disabled = false;

	const brands = useBrandReadQuery();
	const createDomain = useDomainsCreateMutation();
	const deleteDomain = useDomainsRemoveMutation();
	const cloneDomain = useDomainsCloneMutation();
	const allDomainsQuery = useDomainsReadQuery();
	const exportDomains = useDomainsExportMutation();
	const changeDomainStatus = useChangeDomainsStatusMutation();

	$: brandsList = ($brands?.data as any)?.rows?.map((item: any) => ({
		id: item.id,
		key: item.name,
		name: item.name,
		logo: item.logo,
		logo_path: item.logo_path,
		amount: '0'
	}));
	$: currentBrand = ($brands?.data as any)?.rows[0];
	$: domainsList = $allDomainsQuery?.data?.data || [];
	$: totalItems = $allDomainsQuery?.data?.totalItems as number;
	$: isSelectAll = +selectedRowIds.length === +totalItems;
	$: domainUrl = '';

	const callGetData = (data: {
		detail: {
			page_size: number;
			page_number: number;
		};
	}) => {
		pageSize = data?.detail?.page_size;
		pageNumber = data?.detail?.page_number;
		fetchData();
	};

	const checkBoxSelect = (data: { detail: { selectedRowIds: string[] } }) => {
		selectedRowIds = data?.detail.selectedRowIds;
		if (selectedRowIds.length === 0) {
			isSelectAll = false;
		}
	};

	const onAllSelect = () => {
		if (totalItems === selectedRowIds.length) {
			selectedRowIds = [];
		}
		isSelectAll = !isSelectAll;
	};

	const toggleModal = () => {
		isReportDomainOpen = !isReportDomainOpen;
	};

	const searchDebounce = debounce($allDomainsQuery.mutate, 500);

	const onSearch = ({ detail }: CustomEvent<any>) => {
		searchValue = detail.value.trim();
		pageNumber = 1;
		searchDebounce({
			limit: pageSize,
			page: pageNumber,
			search_string: searchValue
		});
	};

	const onBrandSelect = ({ detail: { index } }: CustomEvent<{ index: number }>) => {
		currentBrand = brandsList[index];
	};

	const onDomainInput = ({ detail: { value } }: CustomEvent<{ value: string }>) => {
		domainUrl = value;
	};

	const addReportedDomain = async () => {
		try {
			const selectedBrand = currentBrand || brandsList[0];
			disabled = true;
			const createResult: { success: boolean; message: string; data?: any } =
				await $createDomain.mutateAsync({
					brand_id: selectedBrand.id,
					domain_name: domainUrl
				});
			if (!createResult) {
				disabled = false;
				return (domainError = 'Please check Domain URL');
			}
			if (createResult?.success) {
				showToast({
					title: 'Success',
					description: createResult.message || 'Created Successfully',
					type: 'success'
				});
				fetchData();
				domainUrl = '';
				isReportDomainOpen = false;
			} else {
				domainError = createResult.message;
			}

			disabled = false;
			data.queryClient.invalidateQueries({
				queryKey: ['domains', pageSize, pageNumber]
			});
		} catch (ex) {
			console.error('exception', ex);
		}
	};

	const domainDelteConformationHandler = () => {
		domainDelteConformationModel = !domainDelteConformationModel;
	};

	const domainRemove = async () => {
		if (!itemToBeDeleted) return;
		await $deleteDomain.mutateAsync({ id: itemToBeDeleted });

		data.queryClient.invalidateQueries({
			queryKey: ['domains', pageSize, pageNumber]
		});
		domainDelteConformationHandler();
		fetchData();
		itemToBeDeleted = null;
	};

	const defaultColumns = createDomainColumns({
		onDomainRemove: async (id: string) => {
			domainDelteConformationHandler();
			itemToBeDeleted = id;
		},
		onStatusChagne: async (id: string, status: string) => {
			await $changeDomainStatus.mutateAsync({
				id: id,
				data: {
					status
				}
			});
		}
	});

	const handleExportDomains = async () => {
		const result = await $exportDomains.mutateAsync({
			data: {
				domain_id: selectedRowIds,
				search_string: searchValue
			}
		});

		const csvFile = new Blob([result], { type: 'text/plain' });
		window.URL = window.URL || window.webkitURL;
		const link = document.createElement('a');
		link.setAttribute('href', window.URL.createObjectURL(csvFile));
		link.setAttribute('download', `domais_exports_${Date.now()}.csv`);
		document.body.appendChild(link);

		link.click();
	};

	const fetchData = () => {
		$brands.mutate({});
		$allDomainsQuery.mutate({ limit: pageSize, page: pageNumber, search_string: searchValue });
	};

	onMount(() => {
		fetchData();
	});
</script>

<div class="p-5 pb-0">
	<div class="flex justify-between">
		<DisplayModal
			bind:showModal={isReportDomainOpen}
			modalSize="sm"
			dynamicWidth="500px"
			title={`Domain Takedown`}
			{toggleModal}
			overflow={true}
		>
			<div class="flex flex-col items-start justify-start gap-2.5">
				<Label class="font-inter text-base font-semibold leading-6 text-dark">URL</Label>
				<IconInput
					icon={null}
					value={domainUrl}
					class="w-full rounded-3xl border border-zinc-200 bg-white"
					inputClass="tw-pl-1"
					placeholder="URL"
					on:input={() => (domainError = '')}
					on:input={onDomainInput}
				></IconInput>
			</div>
			<div class="px-3 text-sm text-red-400">{domainError}</div>
			<div class="mt-5 flex w-full items-center gap-5">
				<div
					class="h-[70px] w-[121px] overflow-hidden rounded border border-solid border-border p-1"
				>
					{#if currentBrand?.logo_path != undefined}
						<Image
							class="h-full w-full rounded object-contain object-center"
							src={currentBrand?.logo_path ? currentBrand.logo_path : ''}
						/>
					{/if}
				</div>
				<div class="flex w-full flex-col items-start gap-2.5">
					<Label class="font-inter text-base font-semibold leading-6 text-dark">Brands</Label>
					<div class="relative w-full">
						<Dropdown
							class="inline-flex w-full items-center justify-between rounded-3xl border border-zinc-200 bg-white px-5"
							initialText={brandsList?.[0]?.name}
							initialTextClass="w-full justify-between"
							dropdownClass="w-full justify-between"
							list={brandsList}
							on:select={onBrandSelect}
						/>
					</div>
				</div>
			</div>

			<div class="mt-5 flex items-center gap-2.5">
				<IconButton
					title="Report & Take Down"
					class="w-auto px-5 py-[7px]"
					{disabled}
					variant="danger"
					on:click={addReportedDomain}
				>
					<svelte:fragment slot="icon">
						<StopIcon />
					</svelte:fragment>
				</IconButton>

				<Button
					class="w-auto px-[30px] py-[7px] font-inter leading-[21px] text-dark"
					primary={false}
					onclick={() => {
						domainError = '';
						domainUrl = '';
						toggleModal();
					}}>Cancel</Button
				>
			</div>
		</DisplayModal>

		<div class="flex items-center gap-1 font-inter text-[24px] font-bold text-body-text-color">
			<h4>
				Domains <span class="font-normal text-secondary">({totalItems})</span>
			</h4>
		</div>
		<div class="flex items-center gap-5">
			<div class="relative">
				<IconInput placeholder="Search" class="w-90" on:input={onSearch} bind:value={searchValue}
				></IconInput>
			</div>
			<div class="">
				<Button
					onclick={() => {
						handleExportDomains();
					}}
					class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2.5 tw-self-stretch tw-rounded-2xl tw-border tw-border-primary tw-px-7 tw-py-1.5 tw-text-center tw-text-sm tw-font-medium tw-leading-tight tw-text-neutral-800 !w-auto !bg-primary !font-inter"
				>
					<Export /> <span class="text-white">Export</span>
				</Button>
				<!-- <Button
					onclick={handleExportDomains}
					class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2.5 tw-self-stretch tw-rounded-2xl tw-border tw-border-primary tw-px-7 tw-py-1.5 tw-text-center tw-text-sm tw-font-medium tw-leading-tight tw-text-neutral-800 !w-auto !bg-primary !font-inter"
				>
					<Export /> <span class="text-white">Export</span>
				</Button> -->
			</div>
			<div class="">
				<Button
					onclick={() => {
						isReportDomainOpen = !isReportDomainOpen;
					}}
					primary={false}
					isDelete={true}
					class=" !text-dark"
				>
					<MdBlock /> Report Domain
				</Button>
			</div>
		</div>
	</div>
	<div class="my-5 flex items-center justify-between">
		<div class="">
			<div
				class="flex min-h-[36px] items-center rounded-[22px] border border-border bg-white px-[20px] font-inter text-[14px] font-medium text-body-text-color"
			>
				<CheckboxSingle
					label={`All Select (${isSelectAll ? totalItems : selectedRowIds?.length})`}
					labelClass="ml-2 tw-text-neutral-800 tw-text-sm tw-font-medium tw-leading-tight tw-m-2"
					bind:checked={isSelectAll}
					on:change={onAllSelect}
				></CheckboxSingle>
			</div>
		</div>
	</div>

	<Table
		bind:defaultData={domainsList}
		{defaultColumns}
		dataCount={totalItems}
		selectable={true}
		selectAll={true}
		{selectedRowIds}
		current={pageNumber}
		page_size={pageSize}
		on:toggle={callGetData}
		on:checkbox={checkBoxSelect}
		sortColumWritable={writable()}
	/>
</div>

<Modal showModal={domainDelteConformationModel}>
	<h5 class="text-center">Are you sure, you want to Delete this Domain?</h5>
	<div slot="footer" class="flex justify-around">
		<button
			type="button"
			class="mb-2 me-2 w-2/5 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300"
			on:click={() => {
				itemToBeDeleted = null;
				domainDelteConformationHandler();
			}}
		>
			No
		</button>
		<button
			type="button"
			class="mb-2 me-2 w-2/5 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-800 focus:outline-none focus:ring-4 focus:ring-red-300"
			on:click={() => domainRemove()}
		>
			Yes
		</button>
	</div>
</Modal>
